<template>
  <!-- แถบแจ้งการใช้คุกกี้ (informational) — คุกกี้ที่ใช้เป็นคุกกี้จำเป็น/ที่ผู้ใช้ร้องขอเอง
       จึงเป็นการ "แจ้งให้ทราบ" ไม่ใช่ขอเลือกเปิด-ปิด -->
  <transition name="cookie-banner-slide">
    <section
      v-if="showBanner && !suppressed"
      class="cookie-consent-banner"
      aria-label="Cookie notice"
      data-testid="cookie-consent-banner"
    >
      <div class="cookie-consent-banner__inner">
        <div class="cookie-consent-banner__icon" aria-hidden="true">
          <v-icon size="22">mdi-cookie</v-icon>
        </div>

        <div class="cookie-consent-banner__text">
          <p class="cookie-consent-banner__title">{{ $t('cookieBannerTitle') }}</p>
          <p class="cookie-consent-banner__desc">
            {{ $t('cookieBannerDesc') }}
            <!-- เปิดแท็บใหม่เสมอ — banner โผล่ใน FullStreamV2/snippet iframe ด้วย ถ้า navigate ในแท็บเดิม
                 จะพาแชทออกจากหน้า แล้วต้องพึ่งกลไกกู้ context (fsq) เพื่อกลับมา; แท็บใหม่ตัดปัญหาทั้งหมด -->
            <a :href="cookiePolicyHref" target="_blank" rel="noopener" class="cookie-consent-link">
              {{ $t('cookieBannerPolicyLink') }}
            </a>
          </p>
        </div>

        <div class="cookie-consent-banner__actions">
          <button
            type="button"
            class="cookie-btn cookie-btn--primary"
            data-testid="cookie-accept"
            @click="acknowledge"
          >
            {{ $t('cookieBannerAcknowledge') }}
          </button>
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
import { hasDecision, acknowledgeConsent } from '@/utils/cookieConsent';

// surface ที่ "ไม่ใช่ DGA" — ห้ามแสดง banner (เว็บเจ้าบ้าน/ลูกค้ารายอื่นดูแล cookie notice เอง)
//   - FullStream          = chat v1 ที่ลูกค้ารายอื่น (ไม่ใช่ DGA) ใช้/ฝังอยู่อีกที่
//   - Snippet/SnippetUnauthen/LoginSnippet = widget ฝังแบบเดิม (white-label หลายบริษัท)
// ที่เหลือทั้งหมดถือเป็น DGA → แสดง banner (portal, superadmin, login/register, landing,
// chat v2 รวมถึง v2 ที่ฝังผ่าน JS/iframe)
// FilePreview = แท็บเปิดไฟล์จากลิงก์ในแชท (เข้าจาก widget/snippet) — ไม่ใช่หน้าพอร์ทัล
const NON_DGA_ROUTE_NAMES = new Set(['FullStream', 'Snippet', 'SnippetUnauthen', 'LoginSnippet', 'FilePreview']);

// หน้ารายละเอียดนโยบาย (Terms/Privacy/Cookie) ที่ผู้ใช้ถูกส่งมา "อ่าน" — มักเปิดเป็นแท็บใหม่/ป๊อปอัป
// จาก widget เพื่อดู consent ฉบับเต็ม ไม่ควรมีแถบคุกกี้บังคับกดรับทราบซ้อนทับเนื้อหาอีกชั้น
// (โดยเฉพาะหน้า Cookie policy เอง — banner ลิงก์มาหน้านี้ แล้วยังเด้งทับหน้าที่ตัวเองลิงก์มาก็วนซ้ำ)
// การรับทราบยังเกิดตามปกติบนหน้า DGA อื่น ๆ (Landing หลัก/portal) เมื่อผู้ใช้ไปถึง
const POLICY_DETAIL_ROUTE_NAMES = new Set(['LandingTerms', 'LandingPrivacy', 'LandingCookie']);

export default {
  name: 'CookieConsentBanner',

  data() {
    return {
      showBanner: false,
    };
  },

  computed: {
    // แสดงทั่วทั้งแอป DGA จนกว่าผู้ใช้จะรับทราบ — ซ่อนเฉพาะ surface ที่ไม่ใช่ DGA
    // และหน้ารายละเอียดนโยบาย (อ่าน consent/นโยบายฉบับเต็ม ไม่ต้องกดรับทราบคุกกี้ก่อน)
    suppressed() {
      const name = this.$route?.name || '';
      return NON_DGA_ROUTE_NAMES.has(name) || POLICY_DETAIL_ROUTE_NAMES.has(name);
    },
    cookiePolicyHref() {
      // relative href — snippet ที่ฝังต่าง domain จะ resolve เป็น origin ของ SPA เองซึ่งเสิร์ฟหน้านี้อยู่แล้ว
      return this.$router?.resolve({ name: 'LandingCookie' })?.href || '/Landing/Cookie';
    },
  },

  created() {
    // รอ router resolve route แรกให้เสร็จก่อน (รวม async component ของ route ที่ lazy-load เช่น FullStream)
    // ไม่งั้น $route.name ยังว่างตอน created() ทำให้ suppressed=false ชั่วขณะ → banner โผล่วาบก่อนถูกซ่อน
    this.$router.isReady().then(() => {
      // แสดง banner ถ้ายังไม่เคยรับทราบ (หรือ policy version เปลี่ยน)
      this.showBanner = !hasDecision();
    });
  },

  methods: {
    acknowledge() {
      acknowledgeConsent();
      this.showBanner = false;
    },
  },
};
</script>

<style scoped>
.cookie-consent-banner {
  /* สี accent ของ DGA จาก design tokens (คงที่ทั้งเว็บเพื่อให้ surface แจ้งคุกกี้มีเอกลักษณ์เดียวกัน) */
  --cookie-accent-ink: var(--dga-color-primary);
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  padding: 12px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  pointer-events: none;
}

.cookie-consent-banner__inner {
  pointer-events: auto;
  max-width: 1180px;
  margin: 0 auto;
  background: var(--dga-color-surface, #ffffff);
  border: 1px solid var(--dga-color-divider, rgba(0, 0, 0, 0.1));
  border-radius: var(--dga-radius-lg, 16px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.cookie-consent-banner__icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--dga-color-primary-tonal);
  color: var(--cookie-accent-ink);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cookie-consent-banner__text {
  flex: 1 1 260px;
  min-width: 220px;
}

.cookie-consent-banner__title {
  margin: 0 0 3px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--dga-color-ink, rgba(0, 0, 0, 0.87));
}

.cookie-consent-banner__desc {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--dga-color-ink-muted, rgba(0, 0, 0, 0.65));
}

.cookie-consent-link {
  color: var(--cookie-accent-ink);
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
}

.cookie-consent-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cookie-btn {
  font-family: inherit;
  font-size: 0.8125rem;
  line-height: 1;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background-color 0.15s ease;
  white-space: nowrap;
}

.cookie-btn--primary {
  background: var(--dga-color-primary);
  color: #ffffff;
  font-weight: 500;
}
.cookie-btn--primary:hover {
  background: var(--dga-color-primary-dark);
}

.cookie-banner-slide-enter-active,
.cookie-banner-slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}
.cookie-banner-slide-enter-from,
.cookie-banner-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

@media (max-width: 600px) {
  .cookie-consent-banner__actions {
    width: 100%;
  }
  .cookie-btn {
    flex: 1 1 auto;
    text-align: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cookie-banner-slide-enter-active,
  .cookie-banner-slide-leave-active {
    transition: none;
  }
}
</style>
